import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Search, Pencil, KeyRound, Dice5 } from "lucide-react";
import { generatePassword } from "./utils";

export default function AppUsersTab({ users, onReload }) {
  const [q, setQ] = useState("");
  const [editU, setEditU] = useState(null);

  const filtered = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.toLowerCase();
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(s)
      || (u.email || "").toLowerCase().includes(s)
      || (u.workspace_name || "").toLowerCase().includes(s)
      || (u.role || "").toLowerCase().includes(s)
    );
  }, [users, q]);

  return (
    <div>
      <div className="relative mb-4">
        <Search size={16} className="absolute top-1/2 -translate-y-1/2 left-4 text-[#7A7571]" />
        <input
          data-testid="user-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          className="input-soft !pl-11"
          placeholder="Search by name, email, ID firm, or role…"
        />
      </div>

      <div className="card-soft !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EC] text-[#7A7571]">
            <tr>
              <th className="text-left px-6 py-4 uppercase text-xs tracking-widest font-bold">Name</th>
              <th className="text-left px-6 py-4 uppercase text-xs tracking-widest font-bold hidden sm:table-cell">Email</th>
              <th className="text-left px-6 py-4 uppercase text-xs tracking-widest font-bold hidden md:table-cell">ID firm</th>
              <th className="text-left px-6 py-4 uppercase text-xs tracking-widest font-bold">Role</th>
              <th className="text-right px-6 py-4 uppercase text-xs tracking-widest font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-[#7A7571]">No matching users.</td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.user_id} className="border-t border-[#EBE5D9]" data-testid={`app-user-${u.user_id}`}>
                <td className="px-6 py-4 font-semibold text-[#2C2A29]">{u.name}</td>
                <td className="px-6 py-4 text-[#7A7571] hidden sm:table-cell break-all">{u.email}</td>
                <td className="px-6 py-4 text-[#7A7571] hidden md:table-cell">{u.workspace_name || "—"}</td>
                <td className="px-6 py-4"><span className="badge badge-neutral capitalize">{u.role}</span></td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setEditU(u)} data-testid={`edit-user-${u.user_id}`}
                    className="btn-secondary text-sm !py-1.5 !px-3 inline-flex items-center gap-1">
                    <Pencil size={12} /> Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <EditUserDialog user={editU} open={!!editU} onOpenChange={(v) => !v && setEditU(null)} onDone={onReload} />
    </div>
  );
}

function EditUserDialog({ user, open, onOpenChange, onDone }) {
  const [f, setF] = useState({ name: "", email: "" });
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setF({ name: user.name || "", email: user.email || "" });
    setPw("");
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/superadmin/users/${user.user_id}`, {
        name: f.name, email: f.email.trim().toLowerCase(),
      });
      toast.success("User updated");
      onDone();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (pw.length < 6) { toast.error("At least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/superadmin/users/${user.user_id}/reset-password`, { new_password: pw });
      toast.success("Password reset. Share it with the user securely.");
      setPw("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!user) return null;
  const isGoogle = user.auth_type === "google";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Edit user</DialogTitle>
          <DialogDescription>{user.workspace_name ? `Under ${user.workspace_name}` : "No workspace"} · Role: {user.role}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Name</label>
            <input data-testid="edit-user-name" required className="input-soft" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Email</label>
            <input data-testid="edit-user-email" required type="email" className="input-soft" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          </div>
          <button data-testid="edit-user-save" disabled={busy} className="btn-primary w-full">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </form>

        <div className="card-soft !p-4 !bg-[#F7F4EC] space-y-3 mt-2">
          <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Reset password</div>
          {isGoogle ? (
            <p className="text-sm text-[#7A7571]">This account signs in with Google. Passwords aren't used.</p>
          ) : (
            <>
              <div className="flex gap-2">
                <input data-testid="reset-user-password" type="text" className="input-soft flex-1 font-mono"
                  value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6)" />
                <button type="button" onClick={() => setPw(generatePassword(12))} data-testid="reset-user-genpw"
                  className="btn-secondary !py-2 !px-3 inline-flex items-center gap-1 flex-shrink-0"><Dice5 size={16} /></button>
              </div>
              <button onClick={reset} data-testid="reset-user-submit" disabled={busy || !pw}
                className="btn-primary w-full inline-flex items-center justify-center gap-2">
                <KeyRound size={14} /> {busy ? "Working…" : "Reset password"}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
